import React, { useState, useEffect } from "react";
import { BarChart3, TrendingUp, Calendar, Video, ExternalLink, Megaphone, FileText } from "lucide-react";
import api from "@/lib/api";
import TeaserAuthModal from "@/components/TeaserAuthModal";

const SCORE_OPTIONS = [
  "401 to 425", "426 to 450", "451 to 475", "476 to 500",
  "501 to 525", "526 to 550", "551 to 575", "576 to 600",
  "601 to 615", "616 to 630", "631 to 645", "646 to 660",
  "661 to 670", "671 to 680", "681 to 690", "691 to 700",
  "701 to 705", "706 to 710", "711 to 715", "716 to 720"
];

const ensureAbsoluteUrl = (url) => {
  if (!url) return "";
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
};

export default function GuestPoll() {
  const [currentStep, setCurrentStep] = useState("zone");
  const [selectedZone, setSelectedZone] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingVote, setPendingVote] = useState(null);
  const [portalUpdates, setPortalUpdates] = useState([]);

  useEffect(() => {
    const fetchPortalUpdates = async () => {
      try {
        const res = await api.get("/public/portal-updates");
        setPortalUpdates(res.data);
      } catch (err) {
        console.error("Failed to load portal updates");
      }
    };
    fetchPortalUpdates();
  }, []);

  const ZONES = [
    { id: "400s", color: "#10b981", bg: "rgba(16,185,129,0.1)" },
    { id: "500s", color: "#3b82f6", bg: "rgba(59,130,246,0.1)" },
    { id: "600s", color: "#8b5cf6", bg: "rgba(139,92,246,0.1)" },
    { id: "700s", color: "#f59e0b", bg: "rgba(245,158,11,0.1)" }
  ];
  
  const getRangesForZone = (zone) => {
    switch(zone) {
      case "400s": return SCORE_OPTIONS.slice(0, 4);
      case "500s": return SCORE_OPTIONS.slice(4, 8);
      case "600s": return SCORE_OPTIONS.slice(8, 16);
      case "700s": return SCORE_OPTIONS.slice(16, 20);
      default: return [];
    }
  };

  const handleInteraction = (range) => {
    setPendingVote(range);
    setShowAuthModal(true);
  };

  return (
    <div className="space-y-8 pb-20">
      {/* Poll Card */}
      <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', padding: '2rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: 256, height: 256, background: 'rgba(243,156,18,0.05)', borderRadius: '50%', filter: 'blur(60px)', zIndex: 0 }} />

        <div className="flex items-center gap-3 mb-2" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ padding: 8, background: 'rgba(243,156,18,0.1)', borderRadius: 10 }}>
            <BarChart3 className="h-6 w-6" style={{ color: '#f39c12' }} />
          </div>
          <h2 className="text-xl font-bold" style={{ color: '#f8fafc' }}>NEET 2026 Score Poll</h2>
        </div>
        <p className="text-sm mb-8" style={{ color: '#94a3b8', paddingLeft: 48, position: 'relative', zIndex: 1 }}>
          Join thousands of aspirants! Vote your expected score to see national trends.
        </p>

        <div className="space-y-4" style={{ position: 'relative', zIndex: 1 }}>
          {currentStep === "zone" ? (
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {ZONES.map(zone => (
                <button
                  key={zone.id}
                  onClick={() => { setSelectedZone(zone.id); setCurrentStep("range"); }}
                  className="group transition-all duration-300"
                  style={{ 
                    background: zone.bg, 
                    border: `1px solid ${zone.color}30`, 
                    borderRadius: 20, 
                    padding: '1.25rem 0.5rem', 
                    color: '#f8fafc', 
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = zone.color;
                    e.currentTarget.style.boxShadow = `0 0 20px -5px ${zone.color}40`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = `${zone.color}30`;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] mb-1 transition-colors" style={{ color: zone.color }}>Target Zone</div>
                  <div className="text-2xl md:text-3xl font-black group-hover:scale-110 transition-transform">{zone.id}</div>
                </button>
              ))}
            </div>
          ) : (
            <div className="animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-center gap-2 mb-4">
                <button 
                  onClick={() => setCurrentStep("zone")}
                  style={{ background: 'rgba(255,255,255,0.05)', border: 'none', color: '#94a3b8', padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                >
                  ← Back to Zones
                </button>
                <span className="text-[10px] font-black uppercase tracking-widest text-amber-500">Selected Zone: {selectedZone}</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(() => {
                  const activeZone = ZONES.find(z => z.id === selectedZone) || ZONES[0];
                  return getRangesForZone(selectedZone).map(range => (
                    <button
                      key={range}
                      onClick={() => handleInteraction(range)}
                      className="hover:scale-[1.02] active:scale-[0.98] transition-all group"
                      style={{ 
                        background: activeZone.bg, 
                        border: `1px solid ${activeZone.color}30`, 
                        borderRadius: 12, 
                        padding: '12px 16px', 
                        color: '#f8fafc', 
                        fontSize: 14, 
                        fontWeight: 700, 
                        textAlign: 'left', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      {range}
                      <div 
                        className="h-2 w-2 rounded-full transition-transform group-hover:scale-150" 
                        style={{ 
                          backgroundColor: activeZone.color, 
                          boxShadow: `0 0 8px ${activeZone.color}80` 
                        }} 
                      />
                    </button>
                  ));
                })()}
              </div>
            </div>
          )}

          <button
            onClick={() => handleInteraction('skip')}
            style={{ width: '100%', height: 40, background: 'transparent', border: 'none', color: '#f39c12', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginTop: 12 }}
          >
            Skip and view results
          </button>
        </div>
      </div>

      {/* Latest Updates Feed (Mock) */}
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2 mb-6" style={{ color: '#f8fafc' }}>
          <TrendingUp className="h-6 w-6" style={{ color: '#f39c12' }} />
          Latest Updates
        </h2>

        <div className="space-y-4">
          {portalUpdates.filter(u => u.type === 'UPCOMING_EVENT').map((event, i) => (
            <div key={`event-${i}`} className="relative overflow-hidden group" style={{ background: 'linear-gradient(135deg, #f39c12, #d35400)', borderRadius: 16, padding: '1.5rem', color: '#fff', boxShadow: '0 10px 25px rgba(243,156,18,0.2)' }}>
              <div style={{ position: 'absolute', right: 0, top: 0, width: 128, height: 128, background: 'rgba(255,255,255,0.1)', borderRadius: '50%', filter: 'blur(40px)' }} />
              <div className="flex items-start md:items-center justify-between flex-col md:flex-row gap-4 relative" style={{ zIndex: 1 }}>
                <div className="flex items-center gap-4">
                  <div className="animate-pulse" style={{ height: 48, width: 48, background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Video className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg leading-tight mb-1">{event.title}</h3>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>{event.description || "Medical Admission Guidance Webinar 2026"}</p>
                  </div>
                </div>
                <a
                  href={ensureAbsoluteUrl(event.link)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 font-bold text-sm w-full md:w-auto justify-center"
                  style={{ background: '#fff', color: '#d35400', padding: '10px 20px', borderRadius: 12, textDecoration: 'none', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}
                >
                  <ExternalLink className="h-4 w-4" />
                  {event.link.includes('zoom') ? 'Join Zoom' : 'View Event'}
                </a>
              </div>
            </div>
          ))}

          {/* Fallback mock update if empty */}
          {portalUpdates.length === 0 && (
            <div className="flex items-start gap-4" style={{ background: 'rgba(243,156,18,0.05)', border: '1px solid rgba(243,156,18,0.2)', padding: '1.25rem', borderRadius: 16 }}>
              <div style={{ marginTop: 4, padding: 10, background: 'rgba(243,156,18,0.1)', borderRadius: 12 }}>
                <Megaphone className="h-6 w-6" style={{ color: '#f39c12' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase" style={{ background: '#f39c12', color: '#000' }}>
                    Announcement
                  </span>
                </div>
                <h3 className="font-bold leading-snug mb-1" style={{ color: '#f8fafc' }}>
                  NEET 2026 Registration
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#94a3b8' }}>Information bulletin and application form details will be updated here shortly.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <TeaserAuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} pendingVote={pendingVote} />
    </div>
  );
}
